import { useCallback, useEffect } from "react";

export interface DateSegment {
	start: number;
	end: number;
	token: string;
	value: string;
	length: number;
}

/**
 * Extracts all alphanumeric segments (e.g. '12', '08', '2026') from the input string
 * and maps them to their respective format tokens (e.g. 'dd', 'MM', 'yyyy').
 */
const parseDateSegments = (text: string, dateFormat: string): DateSegment[] => {
	if (!text) {
		return [];
	}

	const formatTokens = dateFormat.match(/d+|M+|y+|Y+|H+|h+|m+|s+|a|A/g) || [];
	const textSegmentRegex = /[a-zA-Z0-9]+/g;
	const segments: DateSegment[] = [];

	let match: RegExpExecArray | null;
	let tokenIndex = 0;

	while ((match = textSegmentRegex.exec(text)) !== null) {
		segments.push({
			start: match.index,
			end: match.index + match[0].length,
			token: formatTokens[tokenIndex] || "UNKNOWN",
			value: match[0],
			length: match[0].length,
		});
		tokenIndex++;
	}

	return segments;
};

/**
 * Replaces a portion of the input string and triggers a synthetic input event
 * so React / Form libraries update their state smoothly.
 */
const updateInputValue = (input: HTMLInputElement, start: number, end: number, replacement: string) => {
	const newValue = input.value.substring(0, start) + replacement + input.value.substring(end);
	input.value = newValue;
	input.dispatchEvent(new Event("input", { bubbles: true }));
};

/**
 * Highlights a specific segment in the text input.
 */
const focusSegment = (input: HTMLInputElement, segment: DateSegment | undefined) => {
	if (segment) {
		input.setSelectionRange(segment.start, segment.end);
	}
};

/**
 * Enhances a DatePicker input with segment-based cursor highlighting,
 * auto-padding (e.g. '1' -> '01'), and automatic jumping between date fields.
 */
export const useDateSegmentSelect = (
	inputRef: React.RefObject<HTMLInputElement | null>,
	dateFormat: string,
	hasValue: boolean,
) => {
	// Helper wrapper around parseDateSegments using the current ref value
	const getSegments = useCallback(() => {
		return parseDateSegments(inputRef.current?.value || "", dateFormat);
	}, [ inputRef, dateFormat ]);

	/**
	 * Highlights the entire segment under the cursor when the user clicks the input.
	 */
	const selectSegmentAtCursor = useCallback((event?: React.MouseEvent<HTMLInputElement>) => {
		const input = inputRef.current;
		if (!input || !hasValue) {
			return;
		}

		// Ignore programmatic clicks (e.g., coordinates 0,0)
		if (event?.nativeEvent?.screenX === 0 && event?.nativeEvent?.screenY === 0) {
			return;
		}

		setTimeout(() => {
			const cursorPosition = input.selectionStart ?? 0;
			const segments = getSegments();
			const activeSegment = segments.find((seg) => cursorPosition >= seg.start && cursorPosition <= seg.end);

			input.focus();
			if (activeSegment) {
				focusSegment(input, activeSegment);
			} else {
				input.select();
			}
		}, 10);
	}, [ inputRef, hasValue, getSegments ]);

	/**
	 * Native Keyboard Event Listener
	 */
	useEffect(() => {
		const input = inputRef.current;
		if (!input) {
			return;
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			const segments = getSegments();
			if (segments.length === 0) {
				return;
			}

			const cursorStart = input.selectionStart ?? 0;
			const cursorEnd = input.selectionEnd ?? 0;

			// Find current active segment index
			const currentIndex = segments.findIndex((seg) => cursorStart >= seg.start && cursorEnd <= seg.end);
			if (currentIndex === -1) {
				return;
			}

			const currentSegment = segments[currentIndex];
			const nextSegment = segments[currentIndex + 1];
			const isYear = /y+/i.test(currentSegment.token);

			// ----------------------------------------------------------------
			// 1. SEPARATOR PRESSED (e.g., '/', '-', '.', ' ')
			// ----------------------------------------------------------------
			const SEPARATORS = [ "-", "/", ".", ":" ];
			if (SEPARATORS.includes(event.key)) {
				event.preventDefault();

				// Auto-pad single digit (e.g., '1' becomes '01')
				if (currentSegment.value.length === 1 && !isYear) {
					updateInputValue(input, currentSegment.start, currentSegment.end, `0${currentSegment.value}`);
				}

				// Jump to the next segment
				if (nextSegment) {
					setTimeout(() => focusSegment(input, getSegments()[currentIndex + 1]), 0);
				}
				return;
			}

			// ----------------------------------------------------------------
			// 2. DIGIT TYPED (0-9)
			// ----------------------------------------------------------------
			if (/^[0-9]$/.test(event.key)) {
				setTimeout(() => {
					const updatedSegments = getSegments();
					const updatedSegment = updatedSegments[currentIndex];
					if (!updatedSegment) {
						return;
					}

					const maxDigits = isYear ? 4 : 2;

					// Jump when digit limit is reached (2 digits for day/month, 4 for year)
					if (updatedSegment.value.length >= maxDigits) {
						if (nextSegment) {
							focusSegment(input, updatedSegments[currentIndex + 1]);
						} else if (isYear) {
							// Deselect at the end of the input if it's the last field
							input.setSelectionRange(updatedSegment.end, updatedSegment.end);
						}
					}
				}, 10);
			}

			// ----------------------------------------------------------------
			// 3. SHORT YEAR AUTO-COMPLETE ('26' + Enter/Tab -> '2026')
			// ----------------------------------------------------------------
			if ((event.key === "Enter" || event.key === "Tab") && isYear && currentSegment.value.length === 2) {
				const century = new Date().getFullYear().toString().substring(0, 2); // '20'
				updateInputValue(input, currentSegment.start, currentSegment.end, `${century}${currentSegment.value}`);
			}
		};

		input.addEventListener("keydown", handleKeyDown);
		return () => input.removeEventListener("keydown", handleKeyDown);
	}, [ inputRef, getSegments ]);

	return { selectSegmentAtCursor };
};

export default useDateSegmentSelect;
